import { styled } from '@mui/material';

import { ExpandIcon } from '../../../icons';
import { Tag } from '../../atoms/Tag';
import { type ListSectionProps, useListSection } from './useListSection';

type Props = ListSectionProps;

const Root = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const Heading = styled('h3')(({ theme }) => ({
  ...theme.typography.h5,
  margin: 0,
  color: theme.palette.text.primary,
}));

const HeaderRow = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
  minHeight: theme.spacing(5.5),
  width: '100%',
}));

const ToggleButton = styled('button')(({ theme }) => ({
  display: 'block',
  width: `calc(100% + ${theme.spacing(2)})`,
  marginInline: theme.spacing(-1),
  paddingInline: theme.spacing(1),
  paddingBlock: 0,
  border: 0,
  borderRadius: theme.shape.borderRadius,
  background: 'none',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Title = styled('span')({
  minWidth: 0,
  overflowWrap: 'anywhere',
});

const End = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: 'auto',
}));

const Summary = styled('span')(({ theme }) => ({
  ...theme.typography.body1Emphasis,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}));

const Chevron = styled(ExpandIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
  flexShrink: 0,
  color: theme.palette.text.secondary,
  transform: expanded ? 'rotate(180deg)' : 'none',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}));

export function ListSection(props: Props) {
  const hook = useListSection(props);

  const headerContent = (
    <HeaderRow>
      <Title>{props.title}</Title>
      <Tag color="default" label={props.count} />
      {props.summary || props.collapsible ? (
        <End>
          {props.summary ? <Summary>{props.summary}</Summary> : null}
          {props.collapsible ? (
            <Chevron expanded={hook.isExpanded} fontSize="small" aria-hidden="true" />
          ) : null}
        </End>
      ) : null}
    </HeaderRow>
  );

  return (
    <Root data-testid={props['data-testid']}>
      <Heading>
        {props.collapsible ? (
          <ToggleButton
            type="button"
            aria-expanded={hook.isExpanded}
            aria-controls={hook.contentId}
            onClick={hook.handleToggle}
          >
            {headerContent}
          </ToggleButton>
        ) : (
          headerContent
        )}
      </Heading>

      <div id={hook.contentId} hidden={!hook.isExpanded}>
        {hook.isExpanded ? props.children : null}
      </div>
    </Root>
  );
}

export type { Props as ListSectionProps };
